// sanitizeImageUrl — THE image-URL string gate for the whole app.
//
// Extracted verbatim (Convention #61 — one rule, not three) from the inner
// `gate()` of sellSheetGeometry.resolveImageUrl, which had been the canonical
// implementation since the 2026-06-02 Sell Sheets v1.2 root-cause fix. Two
// further copies had grown beside it — reportStudio/data/imagePipeline.js (a
// self-documented port) and useModelFamily.cleanImageUrl — and the third had
// already drifted: it accepted `http://` without upgrading it. This module is
// now the single definition; the row-level policies import it.
//
// Rules, unchanged from the original:
//   - non-string / empty            → null
//   - not an http(s) URL            → null  (blocks `javascript:`, bare paths)
//   - `http://`                     → upgraded to `https://`
//
// The http→https upgrade is currently moot for sales_data_master (no http rows
// were found), but it costs nothing and the data can change. It also matters
// independently of the app's CSP: `upgrade-insecure-requests` only applies where
// that header is served, and not every surface renders under it.
export function sanitizeImageUrl(u) {
  if (!u || typeof u !== 'string') return null;
  const t = u.trim();
  if (!t || !t.toLowerCase().startsWith('http')) return null;
  return t.toLowerCase().startsWith('http://') ? 'https://' + t.slice(7) : t;
}

// toSameOriginImageUrl — rewrite a product photo onto OUR origin so the reader's
// browser never contacts the third-party ERP host. This function only rewrites
// the string; the `/img/:path*` rewrite in vercel.json is what actually proxies
// the request at Vercel's edge, so the two must be changed together.
//
// Why SITE_ORIGIN is a hardcoded constant and NOT window.location.origin: the
// first consumer builds EMAIL html that David copies out of the app and sends.
// A localhost or preview origin would produce a URL that works on his screen and
// is dead for every recipient, which is the exact class of bug this fixes. The
// emailed URL must always name production, wherever the app is running.
//
// Host-exact by design. Only www.comax.co.il is rewritten, because that is the
// only host vercel.json points `/img/` at — and the 2026-08-11 census found all
// 15,078 populated image_small values on it. Anything else (Supabase Storage
// item-images, or any host added later) is returned UNCHANGED rather than
// silently routed through a prefix that would 404 it.
//
// SITE_ORIGIN is the CUSTOM domain, deliberately NOT the ...vercel.app alias
// that CLAUDE.md still lists as the "Live URL" and that four auth redirectTo
// constants still use. Measured 2026-08-11: the vercel.app alias answers every
// request — images included — with a 308 to this host. A browser follows that,
// but a mail client is not obliged to, and several refuse redirects on <img>
// entirely. An emailed image URL therefore has to name the canonical host on
// the first hop, so it is one request and cannot be dropped by a strict client.
export const SITE_ORIGIN = 'https://carolinalemkeberlin.site';
const COMAX_HOST = 'www.comax.co.il';

// `relative: true` returns a bare `/img/...` path instead of the absolute form.
// ON SCREEN that is strictly better: it is same-origin on whichever alias the
// user is actually on, so it needs no second DNS lookup and cannot 308. It is
// WRONG for email, where the URL leaves the app entirely and must name the
// canonical host — so absolute stays the DEFAULT and the screen opts out. That
// direction is deliberate: it keeps the email serializer, the one caller whose
// output is unrecallable once sent, byte-for-byte unchanged by this option.
export function toSameOriginImageUrl(u, { relative = false } = {}) {
  const s = sanitizeImageUrl(u);
  if (!s) return null;
  try {
    const url = new URL(s);
    if (url.hostname.toLowerCase() !== COMAX_HOST) return s;
    return (relative ? '' : SITE_ORIGIN) + '/img' + url.pathname + url.search;
  } catch {
    return s;
  }
}
